import type { GeckoKestrelModel, GeckoKestrelModelUnit } from 'types'

export interface ThreeDModelUnitInput {
  id: string
  data: {
    name: string
    matterport_url: string | undefined
    primary_image: string | undefined
  }
}

export interface ThreeDModelSpaceInput {
  id: string
  data: { spaceId: string; displayName: string }
}

export interface ThreeDModelRow {
  key: string
  title: string
  /** The asset this row belongs to; unset for a Matterport space no unit claimed. */
  unitId: string | undefined
  /** The asset's primary photo, the same one the Robot Scans table shows. */
  primaryImageId: string | undefined
  matterportSpaceId: string | undefined
  /** The unit whose `/systemview/:unitId` page shows this asset. */
  systemViewUnitId: string | undefined
  systemViewKestrelModel: GeckoKestrelModel | undefined
}

interface SystemViewTarget {
  unitId: string
  kestrelModel: GeckoKestrelModel
}

export function parseMatterportSpaceId(
  rawUrl: string | undefined
): string | undefined {
  if (!rawUrl) return undefined
  try {
    return new URL(rawUrl).searchParams.get('m') ?? undefined
  } catch {
    return undefined
  }
}

const normalizeName = (name: string): string =>
  name.trim().toLocaleLowerCase().replace(/\s+/g, ' ')

export const threeDModelRowKeyForUnit = (unitId: string): string =>
  `unit:${unitId}`

const compareTitles = (a: ThreeDModelRow, b: ThreeDModelRow): number =>
  a.title.localeCompare(b.title, undefined, {
    numeric: true,
    sensitivity: 'base',
  })

/**
 * A siteview model is tagged with one unit, and only that unit has a System
 * View URL. Every other asset in the model is attached through a
 * kestrel-model-unit link, so those units resolve to the tagged unit's page.
 * Tagged units win over linked ones.
 */
function indexSystemViewTargets(
  siteViewModels: ReadonlyArray<GeckoKestrelModel>,
  kestrelModelUnits: ReadonlyArray<GeckoKestrelModelUnit>
): Map<string, SystemViewTarget> {
  const targetByUnitId = new Map<string, SystemViewTarget>()
  const modelById = new Map<string, GeckoKestrelModel>()

  for (const model of siteViewModels) {
    modelById.set(model.id, model)
    if (model.tags.unit && !targetByUnitId.has(model.tags.unit)) {
      targetByUnitId.set(model.tags.unit, {
        unitId: model.tags.unit,
        kestrelModel: model,
      })
    }
  }

  for (const link of kestrelModelUnits) {
    const model = modelById.get(link.tags['kestrel-model'])
    const linkedUnitId = link.tags.unit
    if (!model?.tags.unit || targetByUnitId.has(linkedUnitId)) continue
    targetByUnitId.set(linkedUnitId, {
      unitId: model.tags.unit,
      kestrelModel: model,
    })
  }

  return targetByUnitId
}

/**
 * One row per asset that has a Matterport walkthrough, a System View model, or
 * both. Matterport spaces are only tagged by site, so they are attached to a
 * unit through the unit's pinned Matterport URL or a matching display name;
 * spaces that match no unit still get their own row. Units that only appear
 * inside a siteview model through a link get a System View square on their
 * row, but not a row of their own.
 */
export function buildThreeDModelRows({
  units,
  matterportSpaces,
  siteViewModels,
  kestrelModelUnits,
}: {
  units: ReadonlyArray<ThreeDModelUnitInput>
  matterportSpaces: ReadonlyArray<ThreeDModelSpaceInput>
  siteViewModels: ReadonlyArray<GeckoKestrelModel>
  kestrelModelUnits: ReadonlyArray<GeckoKestrelModelUnit>
}): ThreeDModelRow[] {
  const systemViewByUnitId = indexSystemViewTargets(
    siteViewModels,
    kestrelModelUnits
  )
  const taggedUnitIds = new Set(
    siteViewModels.map((model) => model.tags.unit).filter(Boolean)
  )

  const spaceByName = new Map<string, ThreeDModelSpaceInput>()
  for (const space of matterportSpaces) {
    const name = normalizeName(space.data.displayName)
    if (name && !spaceByName.has(name)) spaceByName.set(name, space)
  }

  const claimedSpaceIds = new Set<string>()
  const rows: ThreeDModelRow[] = []

  for (const unit of units) {
    const pinnedSpaceId = parseMatterportSpaceId(unit.data.matterport_url)
    const matchedSpace = spaceByName.get(normalizeName(unit.data.name))
    const matterportSpaceId = pinnedSpaceId ?? matchedSpace?.data.spaceId
    const systemView = systemViewByUnitId.get(unit.id)

    if (!matterportSpaceId && !taggedUnitIds.has(unit.id)) continue

    if (matterportSpaceId) claimedSpaceIds.add(matterportSpaceId)

    rows.push({
      key: threeDModelRowKeyForUnit(unit.id),
      title: unit.data.name,
      unitId: unit.id,
      primaryImageId: unit.data.primary_image,
      matterportSpaceId,
      systemViewUnitId: systemView?.unitId,
      systemViewKestrelModel: systemView?.kestrelModel,
    })
  }

  for (const space of matterportSpaces) {
    if (claimedSpaceIds.has(space.data.spaceId)) continue
    claimedSpaceIds.add(space.data.spaceId)
    rows.push({
      key: `space:${space.id}`,
      title: space.data.displayName || 'Untitled walkthrough',
      unitId: undefined,
      primaryImageId: undefined,
      matterportSpaceId: space.data.spaceId,
      systemViewUnitId: undefined,
      systemViewKestrelModel: undefined,
    })
  }

  return rows.sort(compareTitles)
}
